/**
 * Générateur — Connaître la valeur de la somme des mesures des angles d'un triangle
 * Attendu ID : 668 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_drag
 * Labels de référence :
 *   - Connaître la somme des angles d'un triangle (180°)
 *   - Connaître les propriétés angulaires des triangles particuliers
 *   - Vérifier si trois angles peuvent former un triangle
 *   - Associer types de triangles et propriétés angulaires
 *
 * Variantes estimées : 200+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 668;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

const L_SOMME = 'Connaître la valeur de la somme des mesures des angles d\'un triangle';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_SOMME,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// ─── Helpers ─────────────────────────────────────────────────

/** Generate a valid triangle (3 angles summing to 180, all > 0) */
const genValidTriplet = () => {
    const a = randInt(20, 120);
    const b = randInt(10, 170 - a);
    const c = 180 - a - b;
    return [a, b, c];
};

/** Generate an invalid triangle (3 angles NOT summing to 180, all > 0) */
const genInvalidTriplet = () => {
    const offset = randomChoice([-20, -10, 10, 20, 30, -30]);
    const a = randInt(20, 100);
    const b = randInt(20, 100);
    const c = 180 - a - b + offset;
    if (c <= 0 || c > 170) return genInvalidTriplet();
    return [a, b, c];
};

// ─── QCM — 5 sub-variants (weighted toward high-variability) ─

const genQcmPropriete = () => {
    const [A, B, C] = pickLetters(3);
    const formulations = shuffle([
        `<p>Que vaut la somme des mesures des angles d'un triangle ?</p>`,
        `<p>Dans le triangle ${A}${B}${C}, quelle est la valeur de la somme des trois angles ?</p>`,
        `<p>La somme des angles d'un triangle est toujours égale à :</p>`,
    ]);
    const bonne = '180°';
    const piegePools = [['360°', '90°', '270°'], ['90°', '360°', '120°'], ['270°', '120°', '90°'], ['360°', '120°', '60°']];
    const pieges = randomChoice(piegePools);
    // makeQcm attend le CONTENU de la bonne réponse (mélange + lettre gérés par lui)
    const payload = makeQcm([bonne, ...pieges], bonne, formulations[0], opts());
    return payload;
};

const genQcmParticulier = () => {
    const [A, B, C] = pickLetters(3);
    const variante = randomChoice(['equilateral', 'rect_isocele', 'rect_aigus', 'isocele_base']);

    let texte, bonne, pieges;

    if (variante === 'equilateral') {
        texte = randomChoice([
            `<p>Le triangle ${A}${B}${C} est équilatéral. Quelle est la mesure de chaque angle ?</p>`,
            `<p>Le triangle ${A}${B}${C} est équilatéral. Que mesure l'angle en ${A} ?</p>`,
        ]);
        bonne = '60°';
        pieges = shuffle(['90°', '45°', '120°']);
    } else if (variante === 'rect_isocele') {
        texte = randomChoice([
            `<p>Le triangle ${A}${B}${C} est rectangle isocèle en ${A}. Que mesurent les angles aigus ?</p>`,
            `<p>Le triangle ${A}${B}${C} est rectangle isocèle en ${A}. Que mesurent les angles en ${B} et ${C} ?</p>`,
        ]);
        bonne = '45°';
        pieges = shuffle(['60°', '90°', '30°']);
    } else if (variante === 'isocele_base') {
        const sommet = randInt(20, 140);
        if (sommet % 2 !== 0) return genQcmParticulier(); // need even for clean division
        const base = (180 - sommet) / 2;
        texte = `<p>Le triangle ${A}${B}${C} est isocèle en ${A} avec un angle au sommet de ${sommet}°. Que mesure chaque angle de la base ?</p>`;
        bonne = `${base}°`;
        const faux1 = `${180 - sommet}°`;
        const faux2 = `${sommet}°`;
        const faux3 = `${Math.abs(90 - sommet)}°`;
        const pSet = new Set([faux1, faux2, faux3]);
        pSet.delete(bonne);
        pieges = [...pSet].slice(0, 3);
        while (pieges.length < 3) pieges.push(`${randInt(10, 170)}°`);
    } else {
        texte = `<p>Le triangle ${A}${B}${C} est rectangle en ${A}. Que peut-on dire des angles en ${B} et ${C} ?</p>`;
        bonne = 'Ils sont complémentaires (leur somme vaut 90°)';
        pieges = [
            'Ils sont supplémentaires (leur somme vaut 180°)',
            'Ils sont toujours égaux',
            'Leur somme vaut 270°',
        ];
    }

    const payload = makeQcm([bonne, ...pieges], bonne, texte, opts());
    return payload;
};

const genQcmVerification = () => {
    const [A, B, C] = pickLetters(3);
    const isValid = randomChoice([true, false]);
    const triplet = isValid ? genValidTriplet() : genInvalidTriplet();
    const [a, b, c] = triplet;
    const somme = a + b + c;

    const texte = `<p>Un triangle ${A}${B}${C} a des angles mesurant ${a}°, ${b}° et ${c}°. Ces mesures peuvent-elles être celles d'un triangle ?</p>`;

    let bonne, pieges;
    if (isValid) {
        bonne = `Oui, car ${a} + ${b} + ${c} = 180°`;
        pieges = [
            `Non, car ${a} + ${b} + ${c} ≠ 180°`,
            `Oui, car ${a} + ${b} + ${c} = 360°`,
            `Non, car un triangle a forcément un angle droit`,
        ];
    } else {
        bonne = `Non, car ${a} + ${b} + ${c} = ${somme}° ≠ 180°`;
        pieges = [
            `Oui, car ce sont des angles positifs`,
            `Oui, car ${a} + ${b} + ${c} = 180°`,
            `Non, car un triangle ne peut pas avoir d'angle obtus`,
        ];
    }

    const payload = makeQcm([bonne, ...pieges], bonne, texte, opts());
    return payload;
};

const genQcmImpossibilite = () => {
    const [A, B, C] = pickLetters(3);
    const variante = randomChoice([
        'deux_droits', 'equilateral_rect', 'obtus',
        'deux_obtus', 'trois_aigus', 'somme_360',
    ]);

    let bonne, fausses;

    if (variante === 'deux_droits') {
        bonne = `Le triangle ${A}${B}${C} ne peut pas avoir deux angles droits.`;
        fausses = [
            `Le triangle ${A}${B}${C} peut avoir deux angles droits.`,
            `Le triangle ${A}${B}${C} équilatéral a un angle droit.`,
            `La somme des angles du triangle ${A}${B}${C} vaut 360°.`,
        ];
    } else if (variante === 'equilateral_rect') {
        bonne = `Le triangle ${A}${B}${C} ne peut pas être à la fois équilatéral et rectangle.`;
        fausses = [
            `Le triangle ${A}${B}${C} équilatéral est toujours rectangle.`,
            `Le triangle ${A}${B}${C} rectangle a trois angles de 60°.`,
            `Le triangle ${A}${B}${C} isocèle est aussi équilatéral.`,
        ];
    } else if (variante === 'obtus') {
        bonne = `Le triangle ${A}${B}${C} ne peut avoir qu'un seul angle obtus au maximum.`;
        fausses = [
            `Le triangle ${A}${B}${C} peut avoir deux angles obtus.`,
            `Si ${A}${B}${C} est rectangle, il peut aussi avoir un angle obtus.`,
            `Le triangle ${A}${B}${C} isocèle a toujours un angle obtus.`,
        ];
    } else if (variante === 'deux_obtus') {
        const x = randInt(91, 120);
        const y = randInt(91, 120);
        bonne = `Deux angles de ${x}° et ${y}° ne peuvent pas appartenir au même triangle.`;
        fausses = [
            `Deux angles de ${x}° et ${y}° peuvent appartenir au même triangle.`,
            `La somme des angles n'a pas besoin de faire 180°.`,
            `Un triangle peut avoir trois angles obtus.`,
        ];
    } else if (variante === 'trois_aigus') {
        bonne = `Le triangle ${A}${B}${C} peut avoir trois angles aigus.`;
        fausses = [
            `Le triangle ${A}${B}${C} a toujours au moins un angle droit.`,
            `Le triangle ${A}${B}${C} a toujours un angle obtus.`,
            `Le triangle ${A}${B}${C} a toujours exactement un angle de 90°.`,
        ];
    } else {
        bonne = `La somme des angles du triangle ${A}${B}${C} vaut toujours 180°, jamais 360°.`;
        fausses = [
            `La somme des angles du triangle ${A}${B}${C} vaut 360°.`,
            `La somme des angles vaut 180° seulement si ${A}${B}${C} est rectangle.`,
            `La somme des angles de ${A}${B}${C} dépend de la taille du triangle.`,
        ];
    }

    const texte = `<p>Laquelle de ces affirmations concernant le triangle ${A}${B}${C} est correcte ?</p>`;
    const payload = makeQcm([bonne, ...fausses], bonne, texte, opts());
    return payload;
};

const genQcm = () => {
    // Weight toward high-variability sub-variants (verification has random angles+letters)
    const pool = [
        genQcmPropriete,
        genQcmParticulier, genQcmParticulier,
        genQcmVerification, genQcmVerification, genQcmVerification,
        genQcmImpossibilite, genQcmImpossibilite,
    ];
    return randomChoice(pool)();
};

// ─── Saisie ─────────────────────────────────────────────────

const genSaisie = () => {
    const [A, B, C] = pickLetters(3);
    // Weight heavily toward high-variability variants (random angles + letters)
    const variante = randomChoice([
        'somme_contexte', 'somme_contexte', 'somme_contexte',
        'equilateral_contexte', 'equilateral_contexte',
        'rect_isocele_contexte', 'rect_isocele_contexte',
        'rect_complement', 'rect_complement',
        'somme_generique',
    ]);

    let texte, reponse;

    if (variante === 'somme_contexte') {
        const a = randInt(15, 130);
        const b = randInt(10, 170 - a);
        const c = 180 - a - b;
        texte = `<p>Dans le triangle ${A}${B}${C}, les angles mesurent ${a}°, ${b}° et ${c}°. Quelle est la somme des mesures des trois angles (en degrés) ?</p>`;
        reponse = '180';
    } else if (variante === 'equilateral_contexte') {
        texte = `<p>Le triangle ${A}${B}${C} est équilatéral. Quelle est la mesure, en degrés, de l'angle en ${randomChoice([A, B, C])} ?</p>`;
        reponse = '60';
    } else if (variante === 'rect_isocele_contexte') {
        const sommet = randomChoice([A, B, C]);
        const others = [A, B, C].filter(l => l !== sommet);
        texte = `<p>Le triangle ${A}${B}${C} est rectangle isocèle en ${sommet}. Quelle est la mesure, en degrés, de l'angle en ${others[0]} ?</p>`;
        reponse = '45';
    } else if (variante === 'somme_generique') {
        texte = randomChoice([
            `<p>Dans le triangle ${A}${B}${C}, quelle est la valeur, en degrés, de la somme des mesures des trois angles ?</p>`,
            `<p>On considère un triangle ${A}${B}${C} quelconque. Combien vaut, en degrés, la somme de ses angles ?</p>`,
            `<p>Soit ${A}${B}${C} un triangle. La somme de ses trois angles vaut combien de degrés ?</p>`,
        ]);
        reponse = '180';
    } else {
        // rect_complement
        texte = `<p>Le triangle ${A}${B}${C} est rectangle en ${A}. Que vaut, en degrés, la somme des angles en ${B} et ${C} ?</p>`;
        reponse = '90';
    }

    // `suffixe` affiche l'unité À CÔTÉ du champ (SuffixeRenderer) : l'élève voit
    // qu'il n'a que le nombre à taper. Sans lui, celui qui écrit « 180° » était
    // refusé — `compareAnswers` ne retire pas les unités.
    const payload = makeSaisie(reponse, texte, { ...opts(), strict: true, suffixe: '°' });
    return payload;
};

// ─── Relier ─────────────────────────────────────────────────

const genRelier = () => {
    const [A, B, C, D, E, F] = pickLetters(6);
    // Generate random angle pairs for variety
    const a1 = randInt(20, 80);
    const b1 = randInt(20, 160 - a1);
    const c1 = 180 - a1 - b1;
    const a2 = randInt(25, 75);
    const b2 = 90 - a2; // rectangle pair

    const allPairs = shuffle([
        { source: 'Triangle équilatéral', target: 'Trois angles de 60°' },
        { source: 'Triangle rectangle', target: 'Un angle de 90° et deux angles aigus complémentaires' },
        { source: 'Triangle rectangle isocèle', target: 'Angles de 90°, 45° et 45°' },
        { source: 'Triangle isocèle', target: 'Deux angles de la base de même mesure' },
        { source: `Triangle ${A}${B}${C} quelconque`, target: 'Somme des angles = 180°' },
        { source: `Triangle ayant des angles de ${a1}° et ${b1}°`, target: `Le troisième angle mesure ${c1}°` },
        { source: `Triangle ${D}${E}${F} rectangle en ${D}`, target: `Les angles en ${E} et ${F} sont complémentaires` },
        { source: `Triangle rectangle avec un angle de ${a2}°`, target: `L'autre angle aigu mesure ${b2}°` },
    ]);

    // Le pool compte 4 paires « rectangle » sur 8. Sans plafond, près d'un quart
    // des tirages en affichaient 3 ou 4, et 1,2 % n'affichaient QUE des
    // rectangles — alors que la consigne annonce de relier chaque TYPE de
    // triangle. Avec ce plafond il reste toujours deux autres familles à
    // l'écran, et 53 des 70 sous-ensembles de 4 sources restent possibles.
    const rect = allPairs.filter(p => /rectangle/.test(p.source));
    const autres = allPairs.filter(p => !/rectangle/.test(p.source));
    const pairs = shuffle([...rect.slice(0, 2), ...autres]).slice(0, 4);
    const texte = `<p>Relie chaque type de triangle à la propriété angulaire correspondante.</p>`;

    const payload = makeRelier(pairs, texte, opts());
    return payload;
};

// ─── Vrai / Faux ────────────────────────────────────────────

const genVF = () => {
    const [A, B, C] = pickLetters(3);
    const a1 = randInt(20, 80);
    const b1 = randInt(20, 160 - a1);
    const c1 = 180 - a1 - b1;

    const vraisPool = shuffle([
        'La somme des mesures des angles d\'un triangle vaut 180°.',
        'Chaque angle d\'un triangle équilatéral mesure 60°.',
        'Les deux angles aigus d\'un triangle rectangle sont complémentaires.',
        'Un triangle rectangle isocèle a des angles de 45°, 45° et 90°.',
        `Si un angle d'un triangle mesure 90°, alors ce triangle est rectangle.`,
        'Les angles de la base d\'un triangle isocèle sont de même mesure.',
    ]);

    const fauxPool = shuffle([
        'La somme des mesures des angles d\'un triangle vaut 360°.',
        'Un triangle peut avoir deux angles droits.',
        'Chaque angle d\'un triangle équilatéral mesure 90°.',
        'Un triangle rectangle peut aussi être équilatéral.',
        'La somme des angles d\'un triangle vaut 180° uniquement pour les triangles rectangles.',
        'Un triangle isocèle a forcément un angle droit.',
        'Si un triangle a deux angles égaux, alors il est forcément équilatéral.',
        `Les angles 50°, 60° et 80° peuvent former un triangle.`,
    ]);

    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    const selectedVrais = vraisPool.slice(0, nbVrais);
    const selectedFaux = fauxPool.slice(0, nbFaux);

    // Dedup safety
    const allSelected = [...selectedVrais, ...selectedFaux];
    const uniqueSet = new Set(allSelected);
    if (uniqueSet.size < allSelected.length) {
        return genVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `<p>Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts());
    return payload;
};

// ─── Texte à trous (drag) ───────────────────────────────────

const genTrouDrag = () => {
    const [A, B, C] = pickLetters(3);
    const modele = randomChoice(['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7']);

    let phrase, distracteurs;

    if (modele === 'M1') {
        phrase = `Dans le triangle ${A}${B}${C}, la somme des mesures des angles vaut {{180}}°. C'est la mesure d'un angle {{plat}}.`;
        distracteurs = ['360', '90', 'droit', 'aigu'];
    } else if (modele === 'M2') {
        phrase = `Le triangle ${A}${B}${C} est équilatéral : ses trois angles sont {{égaux}} et chacun mesure {{60}}°.`;
        distracteurs = ['isocèle', 'complémentaires', '90', '45'];
    } else if (modele === 'M3') {
        const a = randInt(15, 75);
        const b = 90 - a;
        phrase = `Le triangle ${A}${B}${C} est rectangle en ${A}. Les angles en ${B} et ${C} mesurant ${a}° et ${b}° sont {{complémentaires}} : leur somme vaut {{90}}°.`;
        distracteurs = ['supplémentaires', '180', '60', 'isocèle'];
    } else if (modele === 'M4') {
        phrase = `Le triangle ${A}${B}${C} est rectangle isocèle en ${A}. L'angle en ${A} mesure {{90}}° et les angles en ${B} et ${C} mesurent {{45}}° chacun.`;
        distracteurs = ['60', '120', '180', '30'];
    } else if (modele === 'M5') {
        phrase = `Le triangle ${A}${B}${C} est isocèle en ${A}. Les angles à la base ont la même {{mesure}}. La somme des trois angles vaut {{180}}°.`;
        distracteurs = ['longueur', '360', '90', 'équilatéral'];
    } else if (modele === 'M6') {
        const a = randInt(30, 80);
        const b = randInt(30, 170 - a);
        const c = 180 - a - b;
        phrase = `Dans le triangle ${A}${B}${C}, l'angle en ${A} mesure ${a}° et l'angle en ${B} mesure ${b}°. Comme la somme vaut {{180}}°, l'angle en ${C} mesure {{${c}}}°.`;
        distracteurs = [String(a + b), '360', String(90 - a), String(a)];
    } else {
        // M7: somme angles + nature angle plat — with random triangle name
        phrase = `Le triangle ${A}${B}${C} a {{trois}} angles. La somme de leurs mesures est {{180}}° : c'est un angle plat.`;
        distracteurs = ['quatre', '360', '90', 'deux'];
    }

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une consigne d'une ligne, la phrase à trous reste
    // le coeur du format.
    const texte = '<p>Complète la phrase en glissant les étiquettes.</p>';
    // M6 : les distracteurs calculés (a+b, 90−a, a) peuvent tomber sur la valeur
    // d'un trou (ex. a=43, b=90 → c=47 et 90−a=47) → filtrage systématique.
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(),
        mode: 'drag',
        distracteurs: filtrerDistracteurs(phrase, distracteurs),
    });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    connaissance_somme_angles_qcm_unique: genQcm,
    connaissance_somme_angles_texte: genSaisie,
    connaissance_somme_angles_relier: genRelier,
    connaissance_somme_angles_tableau_vf: genVF,
    connaissance_somme_angles_texte_trou_drag: genTrouDrag,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'connaissance_somme_angles',
        label: 'Connaissance de la somme des angles',
        formatsList: buildFormats('connaissance_somme_angles'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du REGISTRE fait foi, toujours : c'est elle qui est persistée dans
    // `generator_key` et qui identifie le format. Le `if (!...)` était une garde
    // morte — les huit sous-générateurs posaient déjà une clé locale commune,
    // si bien que les cinq formats partageaient une seule et même clé.
    payload._meta.generatorKey = key;
    return payload;
};
